/**
 * Profil -> Hujjatlar tabi (docs/13-hujjatlar.md, 6-bo'lim): hujjatlarni boshqarishning yagona joyi.
 * Har tur bir qator: holat nuqtasi, nomi, meta; o'ngda bitta amal ("Yuklash" yoki "Ochish") va "So'rash"; qatorga faylni
 * sudrab tashlash mumkin; o'ng tugma menyusi -- features/documents/menu.ts. Faylni ochish backend `can_open` bo'yicha
 * (driver egasi yoki sezgir ma'lumot ruxsati), boshqalar faqat holatni ko'radi. Overview'dagi `DocumentsCard` faqat holat.
 *
 * Bir turdan bir nechta nusxa (2026-10-18): qatorda joriy nusxa (backend `is_current`, tur holati shundan), qolgan nusxalar
 * (boshqa shtatdagi yoki eskirgan CDL) uning ostida kichik qatorlar. Fayl bor turga tashlangan fayl yoki "Yana bir nusxa" --
 * yoniga qo'shiladi; "Yangi versiya" (menyu) -- shu nusxani almashtiradi (`replaces`, eskisi arxivga). Ochish -- DocumentPreview.
 */
import { useEffect, useRef, useState, type DragEvent, type MouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { FileText, MoreHorizontal, Plus, Sparkles, Upload } from "lucide-react";
import { ApiError } from "@/lib/api";
import { cn, formatDate } from "@/lib/utils";
import type { DriverDetail } from "@/features/drivers/types";
import { useAppSettings } from "@/features/drivers/api";
import { documentKeys, useArchiveDocument, useDriverDocuments, useRequestDocument, useRerunOcr, useUpdateDocument, useUploadDocument } from "@/features/documents/api";
import { buildRows, copyRow, daysUntil, formatBytes, kindLabel, statusTone, type DocumentRow, type Translate } from "@/features/documents/status";
import { documentMenu } from "@/features/documents/menu";
import { OTHER_KIND, type DocumentType, type DriverDocument } from "@/features/documents/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { ContextMenu } from "@/components/ui/context-menu";
import { confirmDialog } from "@/components/ui/confirm";
import { Field, Input } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { ErrorState, Spinner } from "@/components/ui/misc";
import { toast } from "@/components/ui/toast";
import { OcrSuggestionsModal } from "./OcrSuggestionsModal";
import { DocumentPreview } from "./DocumentPreview";
import { StatusDot } from "./DocumentStatusDot";

export { StatusDot } from "./DocumentStatusDot";

const ACCEPT = "image/*,.heic,.heif,.pdf,application/pdf";
/** Backend `DOCUMENTS_MAX_SIZE` bilan bir xil: kattasi yuklashdan oldin to'xtatiladi (QA 2026-11-02: 25 MB dan kattasini nginx
 * 413 bilan qaytarardi va "Request failed" chiqardi; 20-25 MB esa to'liq yuklanib, keyin rad etilardi) */
const MAX_UPLOAD_MB = 20;
/** Yuklash rejimi: yangi hujjat; shu nusxani almashtirish (eskisi arxivga); mavjudlar yoniga yana bir nusxa */
export type UploadMode = "new" | "replace" | "another";

export function DocumentsTab({ d }: { d: DriverDetail }) {
  const { t } = useTranslation("documents");
  const qc = useQueryClient();
  const docs = useDriverDocuments(d.id);
  const upload = useUploadDocument(d.id);
  const request = useRequestDocument(d.id);
  const archive = useArchiveDocument(d.id);
  const rerun = useRerunOcr(d.id);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [suggest, setSuggest] = useState<DriverDocument | null>(null);
  const [viewer, setViewer] = useState<DriverDocument | null>(null);
  const [dates, setDates] = useState<DriverDocument | null>(null);
  const [otherOpen, setOtherOpen] = useState(false);

  if (docs.isPending) return <div className="skeleton h-[320px] rounded-card" aria-hidden />;
  if (docs.isError) return <ErrorState message={docs.error.message} onRetry={() => docs.refetch()} />;
  const data = docs.data;
  const rows = buildRows(data.types, data.documents);
  const docLabel = (doc: DriverDocument) => (doc.kind === OTHER_KIND ? doc.title || t("kinds.other") : kindLabel(t, doc.kind, doc.label));
  const rowLabel = (row: DocumentRow) => (row.type ? kindLabel(t, row.type.key, row.type.label) : row.doc ? docLabel(row.doc) : "");
  const typeLabel = (key: string) => kindLabel(t, key, data.types.find((x) => x.key === key)?.label ?? key);
  const fail = (err: unknown) =>
    toast.error(err instanceof ApiError ? (err.status === 413 ? t("tooLarge", { mb: MAX_UPLOAD_MB }) : err.message) : t("failed"));
  // Preview'da oldingi/keyingi: fayli bor joriy hujjatlar, keyin eski versiyalar
  const previewDocs = [...data.documents.filter((x) => x.has_file), ...(data.versions ?? [])];

  function doUpload(kind: string, file: File, key: string, title?: string, replaces?: number) {
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(t("tooLarge", { mb: MAX_UPLOAD_MB }));
      return;
    }
    setUploadingKey(key);
    upload.mutate({ kind, file, title, replaces }, { onSuccess: () => toast.success(t("uploaded")), onError: fail, onSettled: () => setUploadingKey(null) });
  }

  function rowUpload(row: DocumentRow, file: File, mode: UploadMode) {
    doUpload(row.kind, file, row.key, row.doc?.title, mode === "replace" && row.doc?.has_file ? row.doc.id : undefined);
  }

  function doRequest(row: DocumentRow) {
    request.mutate(
      { kind: row.kind, title: row.doc?.title },
      { onSuccess: () => toast.success(t("requestedToast"), d.owner ? t("reminderFor", { name: d.owner.display_name }) : undefined), onError: fail },
    );
  }

  async function doArchive(doc: DriverDocument) {
    const ok = await confirmDialog({ title: t("archiveConfirm.title", { label: docLabel(doc) }), description: t("archiveConfirm.description"), confirmLabel: t("actions.archive"), danger: true });
    if (!ok) return;
    archive.mutate(doc.id, {
      onSuccess: () => {
        toast.success(t("archived"));
        setViewer(null);
      },
      onError: fail,
    });
  }

  const rowActions = (row: DocumentRow) => ({
    onUpload: (file: File, mode: UploadMode) => rowUpload(row, file, mode),
    onRequest: () => doRequest(row),
    onOpen: () => row.doc && setViewer(row.doc),
    onEditDates: () => row.doc && setDates(row.doc),
    onArchive: () => row.doc && void doArchive(row.doc),
    onSuggestions: () => row.doc && setSuggest(row.doc),
    onReread: () => row.doc && rerun.mutate(row.doc.id, { onSuccess: () => toast.success(t("ocr.queued")), onError: fail }),
  });

  const s = data.summary;
  const missingNames = s.missing.map(typeLabel);
  const expiredNames = s.expired.map(typeLabel);

  return (
    <>
      <Card>
        <CardHeader
          title={
            <span>
              {t("title")} <span className="tabular font-normal text-muted">{s.received}/{s.total}</span>
            </span>
          }
          subtitle={
            expiredNames.length ? <span className="text-danger">{t("expiredLine", { names: expiredNames.join(", ") })}</span> : missingNames.length ? t("missing", { names: missingNames.join(", ") }) : t("allSet")
          }
          action={
            <Button size="sm" onClick={() => setOtherOpen(true)}>
              <Plus size={12} /> {t("addOther")}
            </Button>
          }
        />
        <CardBody className="pt-1">
          {!data.can_open && <p className="mb-2 text-[12.5px] text-muted">{t("noOpen")}</p>}
          <ul className="divide-y divide-line">
            {rows.map((row) => (
              <DocumentRowView key={row.key} row={row} label={rowLabel(row)} canOpen={data.can_open} uploading={uploadingKey === row.key} {...rowActions(row)}>
                {row.others.map((copy) => {
                  const sub = copyRow(row, copy);
                  return <DocumentRowView key={sub.key} row={sub} label={rowLabel(sub)} canOpen={data.can_open} uploading={uploadingKey === sub.key} compact {...rowActions(sub)} />;
                })}
              </DocumentRowView>
            ))}
          </ul>
          {!!data.versions?.length && (
            <details className="mt-4 text-[13px]">
              <summary className="cursor-pointer font-medium text-muted hover:text-ink">{t("versions", { count: data.versions.length })}</summary>
              <ul className="mt-2 divide-y divide-line">
                {data.versions.map((v) => (
                  <li key={v.id} className="flex items-center gap-3 py-2">
                    <FileText size={14} className="shrink-0 text-muted" aria-hidden />
                    <span className="min-w-0 flex-1 truncate">
                      {docLabel(v)}
                      {v.state ? ` · ${v.state}` : ""} <span className="text-muted">· {formatDate(v.created_at)}{v.uploaded_by ? ` · ${v.uploaded_by.display_name}` : ""}</span>
                    </span>
                    {data.can_open && v.has_file && (
                      <Button size="sm" variant="ghost" onClick={() => setViewer(v)}>
                        {t("actions.open")}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </CardBody>
      </Card>

      <DocumentPreview doc={viewer} docs={previewDocs} label={docLabel} onSelect={setViewer} onClose={() => setViewer(null)} onEditDates={setDates} onArchive={(doc) => void doArchive(doc)} />
      <DatesModal doc={dates} driverId={d.id} onClose={() => setDates(null)} onConflict={() => void qc.invalidateQueries({ queryKey: documentKeys.list(d.id) })} />
      <OcrSuggestionsModal doc={suggest} driver={d} onClose={() => setSuggest(null)} />
      <OtherDocumentModal
        open={otherOpen}
        onOpenChange={setOtherOpen}
        busy={uploadingKey === "other-new"}
        onSubmit={(title, file) => {
          setOtherOpen(false);
          doUpload(OTHER_KIND, file, "other-new", title);
        }}
      />
    </>
  );
}

// ---- Qator ----

interface RowProps {
  row: DocumentRow;
  label: string;
  canOpen: boolean;
  uploading: boolean;
  /** Asosiy qator ostidagi nusxa: kichikroq, so'rash va sudrab tashlash yo'q */
  compact?: boolean;
  children?: ReactNode;
  onUpload: (file: File, mode: UploadMode) => void;
  onRequest: () => void;
  onOpen: () => void;
  onEditDates: () => void;
  onArchive: () => void;
  onSuggestions: () => void;
  onReread: () => void;
}

function DocumentRowView({ row, label, canOpen, uploading, compact, children, onUpload, onRequest, onOpen, onEditDates, onArchive, onSuggestions, onReread }: RowProps) {
  const { t } = useTranslation("documents");
  const inputRef = useRef<HTMLInputElement>(null);
  const modeRef = useRef<UploadMode>("new");
  const [over, setOver] = useState(false);
  const doc = row.doc;
  const hasFile = !!doc?.has_file;
  const pick = (mode: UploadMode) => {
    modeRef.current = mode;
    inputRef.current?.click();
  };
  const download = () => {
    if (doc?.file_url) window.open(`${doc.file_url}?download=1`, "_blank", "noopener");
  };
  const menu = () =>
    documentMenu(
      row,
      canOpen,
      { open: onOpen, download, upload: () => pick(hasFile ? "replace" : "new"), uploadAnother: () => pick("another"), request: onRequest, editDates: onEditDates, archive: onArchive, reread: onReread },
      t,
    );

  function onDrop(e: DragEvent<HTMLLIElement>) {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onUpload(f, hasFile ? "another" : "new");
  }

  function openMenu(e: MouseEvent<HTMLButtonElement>) {
    // Qatorning o'ng tugma menyusini chap tugma bilan ochadi (bir xil amallar, bir joyda)
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.dispatchEvent(new window.MouseEvent("contextmenu", { bubbles: true, cancelable: true, clientX: r.left, clientY: r.bottom + 4 }));
  }

  const tone = statusTone(row.status);
  const hasCopies = row.others.length > 0;
  return (
    <>
      <ContextMenu items={menu}>
        <li
          className={cn("-mx-2 flex items-center rounded-xl px-2 transition-colors", compact ? "gap-3 py-2 pl-[52px]" : "gap-4 py-3", over && "bg-accent-soft")}
          onDragOver={
            compact
              ? undefined
              : (e) => {
                  e.preventDefault();
                  setOver(true);
                }
          }
          onDragLeave={compact ? undefined : () => setOver(false)}
          onDrop={compact ? undefined : onDrop}
        >
          <StatusDot status={row.status} size={compact ? "sm" : "md"} />
          <div className="min-w-0 flex-1">
            <div className={cn("flex flex-wrap items-center gap-x-2 gap-y-0.5 font-medium", compact ? "text-[13.5px]" : "text-[15px]")}>
              <span className="truncate">{label}</span>
              {doc?.state && <span className="rounded-full bg-fill px-1.5 py-px text-[10.5px] font-semibold tracking-[0.04em] text-ink-soft">{doc.state}</span>}
              {!compact && row.type?.required && <span className="text-[10.5px] font-semibold tracking-[0.06em] text-muted uppercase">{t("requiredTag")}</span>}
              {!compact && hasCopies && <span className="text-[10.5px] font-semibold tracking-[0.06em] text-success uppercase">{t("currentTag")}</span>}
              {doc && <OcrChip doc={doc} onOpen={onSuggestions} />}
            </div>
            <div className={cn("mt-0.5", compact ? "text-[12px]" : "text-[13px]", tone === "warning" ? "text-warning" : tone === "danger" ? "text-danger" : "text-muted")}>{metaOf(row, t)}</div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {uploading ? (
              <span className="inline-flex items-center gap-2 pr-2 text-[13px] text-muted">
                <Spinner className="h-4 w-4" /> {t("uploading")}
              </span>
            ) : hasFile ? (
              canOpen && (
                <Button size="sm" variant={compact ? "ghost" : "secondary"} onClick={onOpen}>
                  {t("actions.open")}
                </Button>
              )
            ) : (
              <>
                <Button size="sm" onClick={() => pick("new")}>
                  <Upload size={12} /> {t("actions.upload")}
                </Button>
                {!compact &&
                  (row.status === "requested" ? (
                    <span className="px-2 text-[12.5px] font-semibold text-warning">{t("actions.requested")}</span>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={onRequest}>
                      {t("actions.request")}
                    </Button>
                  ))}
              </>
            )}
            <button
              type="button"
              aria-label={t("actions.more")}
              title={t("actions.more")}
              onClick={openMenu}
              className="grid h-8 w-8 place-items-center rounded-full text-muted transition-colors hover:bg-fill hover:text-ink"
            >
              <MoreHorizontal size={16} aria-hidden />
            </button>
            <input
              ref={inputRef}
              type="file"
              className="sr-only"
              accept={ACCEPT}
              aria-label={t("actions.upload")}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onUpload(f, modeRef.current);
                e.target.value = "";
              }}
            />
          </div>
        </li>
      </ContextMenu>
      {children}
    </>
  );
}

function metaOf(row: DocumentRow, t: Translate): string {
  const doc = row.doc;
  if (!doc || row.status === "missing") return t("status.missing");
  if (row.status === "requested") {
    return [t("status.requested", { when: formatDate(doc.requested_at) }), doc.requested_by ? t("by", { name: doc.requested_by.display_name }) : ""].filter(Boolean).join(" · ");
  }
  const parts = [t("status.received", { when: formatDate(doc.created_at) })];
  if (doc.uploaded_by) parts.push(t("by", { name: doc.uploaded_by.display_name }));
  if (doc.expires_at) {
    if (row.status === "expired") parts.push(t("status.expired", { date: formatDate(doc.expires_at) }));
    else if (row.status === "expiring") parts.push(t("status.expiring", { count: daysUntil(doc.expires_at) }));
    else parts.push(t("expires", { date: formatDate(doc.expires_at) }));
  }
  if (doc.pages > 1) parts.push(t("pages", { count: doc.pages }));
  return parts.join(" · ");
}

/** OCR holati: o'qilmoqda / taklif bor (bosilsa oyna) / ko'rib chiqilgan / o'qilmadi. Ko'ra olmaydiganga faqat holat. */
function OcrChip({ doc, onOpen }: { doc: DriverDocument; onOpen: () => void }) {
  const { t } = useTranslation("documents");
  const base = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold";
  if (doc.ocr_status === "pending") {
    return (
      <span className={cn(base, "bg-fill text-muted")}>
        <Spinner className="h-3 w-3" /> {t("ocr.pending")}
      </span>
    );
  }
  if (doc.ocr_status === "done" && doc.can_open) {
    const reviewed = !!doc.ocr_reviewed_at;
    return (
      <button type="button" onClick={onOpen} className={cn(base, reviewed ? "bg-fill text-ink-soft hover:bg-fill-hover" : "bg-primary-soft text-primary hover:underline")}>
        <Sparkles size={11} aria-hidden /> {reviewed ? t("ocr.done") : t("ocr.suggest")}
      </button>
    );
  }
  if (doc.ocr_status === "done") return <span className={cn(base, "bg-fill text-muted")}>{t("ocr.done")}</span>;
  if (doc.ocr_status === "failed") return <span className={cn(base, "bg-warning-soft text-warning")}>{t("ocr.failed")}</span>;
  return null;
}

// ---- Sanalar (va CDL shtati) ----

function DatesModal({ doc, driverId, onClose, onConflict }: { doc: DriverDocument | null; driverId: number; onClose: () => void; onConflict: () => void }) {
  const { t } = useTranslation("documents");
  const update = useUpdateDocument(driverId);
  const [issued, setIssued] = useState("");
  const [expires, setExpires] = useState("");
  const [title, setTitle] = useState("");
  const [state, setState] = useState("");
  useEffect(() => {
    if (doc) {
      setIssued(doc.issued_at ?? "");
      setExpires(doc.expires_at ?? "");
      setTitle(doc.title);
      setState(doc.state ?? "");
    }
  }, [doc]);
  const isCdl = doc?.kind === "cdl";

  function save() {
    if (!doc) return;
    update.mutate(
      { id: doc.id, version: doc.version, issued_at: issued || null, expires_at: expires || null, ...(doc.kind === OTHER_KIND ? { title } : {}), ...(isCdl ? { state } : {}) },
      {
        onSuccess: () => {
          toast.success(t("dates.saved"));
          onClose();
        },
        onError: (err) => {
          if (err instanceof ApiError && err.isConflict) {
            toast.warning(t("conflict"));
            onConflict();
            onClose();
            return;
          }
          toast.error(err instanceof ApiError ? err.message : t("failed"));
        },
      },
    );
  }

  return (
    <Modal
      open={!!doc}
      onOpenChange={(o) => !o && onClose()}
      title={t("dates.title")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dates.cancel")}
          </Button>
          <Button variant="ink" onClick={save} loading={update.isPending}>
            {t("dates.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {doc?.kind === OTHER_KIND && (
          <Field label={t("otherName")} htmlFor="doc-title">
            <Input id="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
          </Field>
        )}
        {isCdl && (
          <Field label={t("dates.state")} htmlFor="doc-state">
            <Input id="doc-state" value={state} onChange={(e) => setState(e.target.value.replace(/[^a-z]/gi, "").toUpperCase().slice(0, 2))} placeholder={t("dates.statePlaceholder")} maxLength={2} className="w-24 uppercase" autoComplete="off" />
          </Field>
        )}
        <Field label={t("dates.issued")} htmlFor="doc-issued">
          <Input id="doc-issued" type="date" value={issued} onChange={(e) => setIssued(e.target.value)} />
        </Field>
        <Field label={t("dates.expires")} htmlFor="doc-expires">
          <Input id="doc-expires" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---- Boshqa hujjat ----

function OtherDocumentModal({ open, onOpenChange, busy, onSubmit }: { open: boolean; onOpenChange: (o: boolean) => void; busy: boolean; onSubmit: (title: string, file: File) => void }) {
  const { t } = useTranslation("documents");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) {
      setTitle("");
      setFile(null);
    }
  }, [open]);
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("addOther")}
      description={t("accepted")}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("dates.cancel")}
          </Button>
          <Button variant="ink" disabled={!title.trim() || !file} loading={busy} onClick={() => file && onSubmit(title.trim(), file)}>
            {t("actions.upload")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t("otherName")} required htmlFor="other-title">
          <Input id="other-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} autoFocus />
        </Field>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => inputRef.current?.click()}>
            <Upload size={12} /> {file ? t("changeFile") : t("chooseFile")}
          </Button>
          {file && (
            <span className="text-[13px] text-muted">
              {file.name} · {formatBytes(file.size)}
            </span>
          )}
          <input ref={inputRef} type="file" className="sr-only" accept={ACCEPT} aria-label={t("chooseFile")} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </div>
      </div>
    </Modal>
  );
}

// ---- Overview kartasi: faqat holat, boshqaruv tabda ----

export function DocumentsCard({ d, onOpen }: { d: DriverDetail; onOpen: () => void }) {
  const { t } = useTranslation("documents");
  const settings = useAppSettings();
  const types = (settings.data?.document_types ?? []) as DocumentType[];
  const s = d.documents_summary;
  const typeLabel = (key: string) => kindLabel(t, key, types.find((x) => x.key === key)?.label ?? key);
  const missing = s.missing.map(typeLabel);
  const expired = s.expired.map(typeLabel);
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">{t("title")}</div>
        <div className="tabular text-[13px] font-semibold">
          {s.received}/{s.total}
        </div>
      </div>
      {types.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {types.map((type) => (
            <span key={type.key} className="inline-flex items-center gap-1.5 rounded-full bg-fill px-2.5 py-1 text-[12px] font-medium text-ink-soft">
              <StatusDot status={s.statuses[type.key] ?? "missing"} size="sm" /> {kindLabel(t, type.key, type.label)}
            </span>
          ))}
        </div>
      )}
      <p className={cn("mt-3 text-[13px]", expired.length ? "text-danger" : missing.length ? "text-warning" : "text-muted")}>
        {expired.length ? t("expiredLine", { names: expired.join(", ") }) : missing.length ? t("missing", { names: missing.join(", ") }) : t("allSet")}
      </p>
      <Button size="sm" className="mt-3" onClick={onOpen}>
        {t("overview.open")}
      </Button>
    </Card>
  );
}
